import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Row, Col, Card, CardBody, Spinner } from "reactstrap";
import Chart from "react-apexcharts";
import TableContainer from "../../../components/Common/TableContainer";
import { getSubjectWiseSummaryReport, getLoggedInUser } from "../../../helpers/fakebackend_helper";
import "./subjectWiseSummary.scss";

// Validated categorical / status colors (see dataviz skill reference palette.md)
const palette = {
  blue: "#2a78d6",
  orange: "#eb6834",
  aqua: "#1baf7a",
  violet: "#4a3aa7",
  magenta: "#e87ba4",
  good: "#0ca30c",
  warning: "#fab219",
  critical: "#d03b3b",
  muted: "#898781",
  grid: "#e1e0d9",
};

const truncateText = (text, maxLen = 28) =>
  text && text.length > maxLen ? `${text.slice(0, maxLen).trimEnd()}...` : text;

const kpiMeta = [
  { key: "totalSubjects", label: "Total Subjects", sub: "All Subjects", icon: "mdi mdi-book-open-page-variant", bg: palette.blue },
  { key: "totalQuestions", label: "Total Questions", sub: "Total Questions", icon: "mdi mdi-help-circle-outline", bg: palette.good },
  { key: "activeSubjects", label: "Active Subjects", sub: "Active Subjects", icon: "mdi mdi-account-outline", bg: palette.warning },
  { key: "submitTotal", label: "Submit for Approval", sub: "Questions", icon: "mdi mdi-timer-sand", bg: palette.critical },
  { key: "pendingTotal", label: "Pending for Approval", sub: "Questions", icon: "mdi mdi-progress-clock", bg: palette.violet },
  { key: "approvedTotal", label: "Approved", sub: "Questions", icon: "mdi mdi-check-circle-outline", bg: palette.aqua },
];

const topSubjectColors = [palette.blue, palette.good, palette.violet, palette.orange, palette.magenta];

const CardHeader = ({ icon, title, color }) => (
  <div className="swsr-card-header" style={{ backgroundColor: `${color}1a` }}>
    <div className="swsr-card-header-left">
      <div className="swsr-card-header-icon" style={{ backgroundColor: color }}>
        <i className={icon}></i>
      </div>
      <div className="swsr-card-header-title">{title}</div>
    </div>
  </div>
);

const SubjectWiseSummaryReport = () => {
  const [reportData, setReportData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [loadError, setLoadError] = useState(false);

  const fetchReport = useCallback(async () => {
    setLoadError(false);
    try {
      const user = getLoggedInUser();
      const response = await getSubjectWiseSummaryReport({ user_id: Number(user.uid) });
      if (!response || response.statusFlag === 2 || response.status === '401' || response.status === 401) {
        setLoadError(true);
        setReportData(null);
      } else {
        setReportData(response);
      }
    } catch (err) {
      setLoadError(true);
      setReportData(null);
    } finally {
      setIsLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    document.title = "Reports | Subject Wise Summary";
    const headerEl = document.getElementById("headerTitle");
    if (headerEl) headerEl.innerHTML = "Subject Wise Summary Report";
    fetchReport();
  }, [fetchReport]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchReport();
  };

  const subjectRows = useMemo(
    () => (reportData?.subjectRows || []).map((row, idx) => ({ sno: idx + 1, ...row })),
    [reportData]
  );

  const topSubjects = useMemo(
    () => (reportData?.topSubjects || []).map((item, idx) => ({ ...item, bg: topSubjectColors[idx % topSubjectColors.length] })),
    [reportData]
  );

  const totalQuestions = reportData?.totalQuestions || 0;
  const approvedTotal = reportData?.approvedTotal || 0;
  const submitTotal = reportData?.submitTotal || 0;
  const pendingTotal = reportData?.pendingTotal || 0;
  const tamilQuestions = reportData?.tamilQuestions || 0;
  const englishQuestions = reportData?.englishQuestions || 0;

  const barOptions = {
    chart: { type: "bar", toolbar: { show: false }, fontFamily: "inherit" },
    plotOptions: { bar: { horizontal: true, barHeight: "55%", borderRadius: 4 } },
    dataLabels: { enabled: false },
    xaxis: {
      categories: reportData?.barCategories || [],
      labels: { style: { colors: palette.muted } },
    },
    yaxis: {
      labels: { style: { colors: palette.muted }, formatter: (val) => (typeof val === "string" ? truncateText(val, 20) : val) },
    },
    grid: { borderColor: palette.grid, xaxis: { lines: { show: true } }, yaxis: { lines: { show: false } } },
    colors: [palette.blue],
    tooltip: { y: { formatter: (val) => `${val.toLocaleString()} questions` } },
  };
  const barSeries = [{ name: "Questions", data: reportData?.barValues || [] }];

  const donutLanguageOptions = {
    chart: { type: "donut", fontFamily: "inherit" },
    labels: ["Tamil", "English"],
    colors: [palette.blue, palette.orange],
    legend: { show: false },
    dataLabels: { enabled: false },
    stroke: { width: 2, colors: ["#fff"] },
    tooltip: { y: { formatter: (val) => `${val}%` } },
    plotOptions: {
      pie: {
        donut: {
          size: "70%",
          labels: {
            show: true,
            total: {
              show: true,
              label: "Questions",
              formatter: () => totalQuestions.toLocaleString(),
            },
          },
        },
      },
    },
  };
  const donutLanguageSeries = totalQuestions
    ? [
        Number(((tamilQuestions / totalQuestions) * 100).toFixed(1)),
        Number(((englishQuestions / totalQuestions) * 100).toFixed(1)),
      ]
    : [0, 0];

  const donutStatusOptions = {
    chart: { type: "donut", fontFamily: "inherit" },
    labels: ["Approved", "Submit for Approval", "Pending for Approval"],
    colors: [palette.good, palette.warning, palette.critical],
    legend: { show: false },
    dataLabels: { enabled: false },
    stroke: { width: 2, colors: ["#fff"] },
    tooltip: { y: { formatter: (val) => `${val}%` } },
    plotOptions: {
      pie: {
        donut: {
          size: "70%",
          labels: {
            show: true,
            total: {
              show: true,
              label: "Questions",
              formatter: () => totalQuestions.toLocaleString(),
            },
          },
        },
      },
    },
  };
  const donutStatusSeries = totalQuestions
    ? [
        Number(((approvedTotal / totalQuestions) * 100).toFixed(1)),
        Number(((submitTotal / totalQuestions) * 100).toFixed(1)),
        Number(((pendingTotal / totalQuestions) * 100).toFixed(1)),
      ]
    : [0, 0, 0];

  const difficultyCategories = reportData?.difficultyCategories || [];
  // API doesn't return an "advanced" bucket yet - placeholder zeros until that lands server-side.
  const advancedData = difficultyCategories.map(() => 0);

  const difficultyOptions = {
    chart: { type: "bar", stacked: true, stackType: "100%", toolbar: { show: false }, fontFamily: "inherit" },
    plotOptions: { bar: { horizontal: true, barHeight: "55%" } },
    dataLabels: { enabled: false },
    xaxis: { categories: difficultyCategories, labels: { style: { colors: palette.muted } } },
    yaxis: {
      labels: { style: { colors: palette.muted }, formatter: (val) => (typeof val === "string" ? truncateText(val, 20) : val) },
    },
    grid: { borderColor: palette.grid, xaxis: { lines: { show: false } }, yaxis: { lines: { show: false } } },
    colors: [palette.good, palette.warning, palette.critical, palette.muted],
    legend: { show: false },
    tooltip: { y: { formatter: (val) => `${val}%` } },
  };
  const difficultySeries = [
    { name: "Easy", data: reportData?.easyData || [] },
    { name: "Medium", data: reportData?.mediumData || [] },
    { name: "Difficult", data: reportData?.hardData || [] },
    { name: "Advanced", data: advancedData },
  ];

  const columns = useMemo(
    () => [
      { header: "#", accessorKey: "sno", enableColumnFilter: false, enableSorting: false, width: "5%" },
      {
        header: "Subject", accessorKey: "subject", enableColumnFilter: false, enableSorting: true,
        cell: (cellProps) => <span title={cellProps.getValue()}>{truncateText(cellProps.getValue(), 40)}</span>,
      },
      {
        header: "Tamil Questions", accessorKey: "tamil", enableColumnFilter: false, enableSorting: true,
        cell: (cellProps) => cellProps.getValue().toLocaleString(),
      },
      {
        header: "English Questions", accessorKey: "english", enableColumnFilter: false, enableSorting: true,
        cell: (cellProps) => cellProps.getValue().toLocaleString(),
      },
      {
        header: "Total Questions", accessorKey: "total", enableColumnFilter: false, enableSorting: true,
        cell: (cellProps) => <strong>{cellProps.getValue().toLocaleString()}</strong>,
      },
      {
        header: "Approved", accessorKey: "approved", enableColumnFilter: false, enableSorting: true,
        cell: (cellProps) => <span style={{ color: palette.good }}>{cellProps.getValue().toLocaleString()}</span>,
      },
      {
        header: "Submit for Approval", accessorKey: "submit", enableColumnFilter: false, enableSorting: true,
        cell: (cellProps) => <span style={{ color: palette.warning }}>{cellProps.getValue().toLocaleString()}</span>,
      },
      {
        header: "Pending for Approval", accessorKey: "pending", enableColumnFilter: false, enableSorting: true,
        cell: (cellProps) => <span style={{ color: palette.critical }}>{cellProps.getValue().toLocaleString()}</span>,
      },
      {
        header: "Status", accessorKey: "status", enableColumnFilter: false, enableSorting: false,
        cell: (cellProps) => (
          <span className={cellProps.getValue() === "Active" ? "swsr-badge-active" : "swsr-badge-inactive"}>
            {cellProps.getValue()}
          </span>
        ),
      },
      {
        header: "Actions", enableColumnFilter: false, enableSorting: false, width: "6%",
        cell: () => (
          <div className="btn btn-sm btn-soft-info">
            <i className="mdi mdi-eye-outline" />
          </div>
        ),
      },
    ],
    []
  );

  if (isLoading) {
    return (
      <div className="page-content">
        <div className="container-fluid text-center py-5">
          <Spinner color="primary" />
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="page-content">
        <div className="container-fluid text-center py-5">
          <i className="mdi mdi-cloud-alert-outline" style={{ fontSize: "48px", color: palette.critical }}></i>
          <p className="mt-2 mb-3">Couldn't load the report. Please try again.</p>
          <button type="button" className="swsr-refresh-btn" onClick={fetchReport}>
            <i className="mdi mdi-refresh"></i> Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="page-content">
      <div className="container-fluid">
        <div className="swsr-toolbar">
          <button type="button" className="swsr-refresh-btn" onClick={handleRefresh} disabled={refreshing}>
            <i className={`mdi mdi-refresh ${refreshing ? "swsr-spin" : ""}`}></i> Refresh
          </button>
        </div>

        <Row className="g-3">
          {kpiMeta.map((meta, idx) => (
            <Col lg={2} md={4} sm={6} xs={12} key={idx}>
              <Card className="swsr-kpi-card">
                <CardBody className="d-flex align-items-center gap-3">
                  <div className="swsr-kpi-icon" style={{ backgroundColor: meta.bg }}>
                    <i className={meta.icon}></i>
                  </div>
                  <div>
                    <div className="swsr-kpi-value">{Number(reportData?.[meta.key] || 0).toLocaleString()}</div>
                    <div className="swsr-kpi-label">{meta.sub}</div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          ))}
        </Row>

        <Row className="g-3 mt-1">
          <Col xl={6}>
            <Card className="swsr-chart-card">
              <CardBody>
                <CardHeader icon="mdi mdi-poll" title="Questions by Subject" color={palette.blue} />
                <Chart options={barOptions} series={barSeries} type="bar" height={300} />
              </CardBody>
            </Card>
          </Col>
          <Col xl={6}>
            <Card className="swsr-chart-card">
              <CardBody>
                <CardHeader icon="mdi mdi-signal-cellular-3" title="Difficulty Level by Subject" color={palette.violet} />
                <Chart options={difficultyOptions} series={difficultySeries} type="bar" height={300} />
                <div className="swsr-chart-legend">
                  <span><span className="swsr-legend-dot" style={{ backgroundColor: palette.good }}></span>Easy</span>
                  <span><span className="swsr-legend-dot" style={{ backgroundColor: palette.warning }}></span>Medium</span>
                  <span><span className="swsr-legend-dot" style={{ backgroundColor: palette.critical }}></span>Difficult</span>
                  <span><span className="swsr-legend-dot" style={{ backgroundColor: palette.muted }}></span>Advanced</span>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        <Row className="g-3 mt-1">
          <Col xl={3} md={6}>
            <Card className="swsr-chart-card">
              <CardBody>
                <CardHeader icon="mdi mdi-translate" title="Language Wise Distribution" color={palette.aqua} />
                <Chart options={donutLanguageOptions} series={donutLanguageSeries} type="donut" height={230} />
                <div className="swsr-chart-legend">
                  <span><span className="swsr-legend-dot" style={{ backgroundColor: palette.blue }}></span>Tamil ({tamilQuestions.toLocaleString()})</span>
                  <span><span className="swsr-legend-dot" style={{ backgroundColor: palette.orange }}></span>English ({englishQuestions.toLocaleString()})</span>
                </div>
              </CardBody>
            </Card>
          </Col>
          <Col xl={3} md={6}>
            <Card className="swsr-chart-card">
              <CardBody>
                <CardHeader icon="mdi mdi-chart-donut" title="Status Wise Questions" color={palette.warning} />
                <Chart options={donutStatusOptions} series={donutStatusSeries} type="donut" height={230} />
                <div className="swsr-chart-legend">
                  <span><span className="swsr-legend-dot" style={{ backgroundColor: palette.good }}></span>Approved</span>
                  <span><span className="swsr-legend-dot" style={{ backgroundColor: palette.warning }}></span>Submit</span>
                  <span><span className="swsr-legend-dot" style={{ backgroundColor: palette.critical }}></span>Pending</span>
                </div>
              </CardBody>
            </Card>
          </Col>
          <Col xl={3} md={6}>
            <Card className="swsr-chart-card">
              <CardBody>
                <CardHeader icon="mdi mdi-history" title="Recently Updated Subjects" color={palette.good} />
                {(reportData?.recentlyUpdated || []).map((item, idx) => (
                  <div className="swsr-side-list-item" key={idx}>
                    <span className="swsr-truncate" title={item.name}>{item.name}</span>
                    <span className="text-muted flex-shrink-0 ms-2">{item.time}</span>
                  </div>
                ))}
              </CardBody>
            </Card>
          </Col>
          <Col xl={3} md={6}>
            <Card className="swsr-chart-card">
              <CardBody>
                <CardHeader icon="mdi mdi-trophy-outline" title="Top 5 Subjects by Questions" color={palette.magenta} />
                {topSubjects.map((item, idx) => (
                  <div className="swsr-side-list-item" key={idx}>
                    <span className="d-flex align-items-center" style={{ minWidth: 0 }}>
                      <span className="swsr-side-badge" style={{ backgroundColor: item.bg }}>{idx + 1}</span>
                      <span className="swsr-truncate" title={item.name}>{item.name}</span>
                    </span>
                    <span className="text-muted flex-shrink-0 ms-2">{Number(item.value).toLocaleString()}</span>
                  </div>
                ))}
              </CardBody>
            </Card>
          </Col>
        </Row>

        <Row className="mt-3">
          <Col xs="12">
            <Card className="swsr-chart-card">
              <CardBody>
                <CardHeader icon="mdi mdi-table-large" title="Subject Summary" color={palette.blue} />
                <TableContainer
                  columns={columns}
                  data={subjectRows}
                  isGlobalFilter={true}
                  SearchPlaceholder="Search subject..."
                  isCustomPageSize={true}
                  isPagination={true}
                  divClassName="table-responsive"
                  tableClass="align-middle table-nowrap"
                  theadClass="table-light"
                  paginationWrapper="dataTables_paginate paging_simple_numbers"
                  pagination="pagination pagination-rounded justify-content-end mb-0"
                />
              </CardBody>
            </Card>
          </Col>
        </Row>

        <div className="swsr-footer-legend">
          <div className="swsr-footer-legend-items">
            <span><span className="swsr-legend-dot" style={{ backgroundColor: palette.good }}></span>Approved - Questions approved and published</span>
            <span><span className="swsr-legend-dot" style={{ backgroundColor: palette.warning }}></span>Submit for Approval - Sent for review by admin</span>
            <span><span className="swsr-legend-dot" style={{ backgroundColor: palette.critical }}></span>Pending for Approval - Awaiting admin action</span>
          </div>
          <div>Last updated {reportData?.lastUpdated || "-"}</div>
        </div>
      </div>
    </div>
  );
};

export default SubjectWiseSummaryReport;
